import { Login as LoginIcon } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, Link, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { JSX, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TenantSelectionRequiredError, useAuth } from '../../contexts/AuthContext';
import { forgotPasswordLink, landingLinkFor, registerLink, selfRegistrationEnabled } from '../../library/constants';
import { fieldProps, useValidatedForm } from '../../library/formValidation';
import { emailAdornment, getVisibilityButton } from '../../library/formsHelper';
import { TenantSummary, UserLoginRequest } from '../../types';
import { cardContentStyles, cardStyles, clinicFieldStyles, getForgotPasswordLinkStyles, getRegisterLinkStyles, headerBoxStyles, linkBoxStyles, pageWrapperStyles, passwordFieldStyles, registerBoxStyles, signInButtonStyles, usernameFieldStyles, } from './loginForm.styles';
import { createLoginSchema } from './loginSchema';
import ResourcesIds from '../../resources/ResourcesIds';
import { useTranslation } from 'react-i18next';
import React from 'react';

export const LoginForm = (): JSX.Element => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isLoading, error, hasFailed, sessionEnded } = useAuth();
    const { t: translate } = useTranslation();

    const schema = React.useMemo(() => createLoginSchema(translate), [translate]);

    const { register, handleSubmit, watch, formState: { errors, isValid, isSubmitting } } =
        useValidatedForm<UserLoginRequest>(schema, {
            defaultValues: { emailAddress: '', password: '' },
        });

    const [showPassword, setShowPassword] = useState(false);

    // Only ever set for an account that belongs to several clinics, and only after the
    // password has already been accepted -- the server refuses to list anyone's clinics
    // before that, so there is nothing to show until the first attempt comes back.
    const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
    const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('');

    const emailAddress = watch('emailAddress');

    // The list belongs to the address that produced it. Someone who corrects a typo after
    // seeing the picker would otherwise resubmit against another account's clinics and be
    // turned away with "You do not have access to the requested clinic."
    useEffect(() => {
        setTenants(null);
        setSelectedTenantId('');
    }, [emailAddress]);

    const onSubmit = async (data: UserLoginRequest) => {
        try {
            // The role comes from the response, not from the context: the dispatch that
            // stores it has not been applied yet at this point.
            const { role } = await login({ ...data, tenantId: selectedTenantId || undefined });

            // Both admin roles have a console of their own and neither can use the
            // clinic-facing pages, so a remembered return path is ignored for them: it is
            // almost always the page a guard just turned them away from, and honouring it
            // would only bounce them straight back to where they started.
            const rememberedPath = role === 'SUPER_ADMIN' || role === 'ADMIN'
                ? undefined
                : location.state?.from?.pathname;

            navigate(rememberedPath || landingLinkFor(role), { replace: true });
        } catch (error) {
            // Not a failed sign-in: the credentials were fine and the server is only asking
            // which clinic this session is for. Show the picker and let them submit again.
            if (error instanceof TenantSelectionRequiredError) {
                setTenants(error.tenants);
                setSelectedTenantId('');
                return;
            }

            // anything else is handled by auth context
        }
    };

    return (
        <Box sx={pageWrapperStyles}>
            <Card sx={cardStyles}>
                <CardContent sx={cardContentStyles}>
                    <Box sx={headerBoxStyles}>
                        <Typography variant="h4" component="h1" gutterBottom>{translate(ResourcesIds.LOGIN_TITLE)}</Typography>
                        <Typography variant="body1" color="text.secondary">{translate(ResourcesIds.LOGIN_SUBTITLE)}</Typography>
                    </Box>

                    {/* Why somebody who was working a moment ago is looking at a sign-in form.
                        Gone as soon as they try: from then on the attempt is the news. */}
                    {sessionEnded && !hasFailed && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate(ResourcesIds.SESSION_ENDED_NOTICE)}
                        </Alert>
                    )}

                    {hasFailed && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error ?? translate(ResourcesIds.LOGIN_FAILED_DEFAULT)}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            {...register('emailAddress')}
                            fullWidth
                            label={translate(ResourcesIds.EMAIL_LABEL)}
                            {...fieldProps(errors, 'emailAddress')}
                            margin="normal"
                            slotProps={{ input: { startAdornment: emailAdornment, } }}
                            sx={usernameFieldStyles}
                        />

                        <TextField
                            {...register('password')}
                            fullWidth
                            label={translate(ResourcesIds.PASSWORD_LABEL)}
                            type={showPassword ? 'text' : 'password'}
                            {...fieldProps(errors, 'password')}
                            margin="normal"
                            slotProps={{ input: { endAdornment: (getVisibilityButton(showPassword, () => setShowPassword(!showPassword))), } }}
                            sx={passwordFieldStyles}
                        />

                        {tenants && (
                            <TextField
                                select
                                fullWidth
                                label={translate(ResourcesIds.CLINIC_LABEL)}
                                value={selectedTenantId}
                                onChange={(event) => setSelectedTenantId(Number(event.target.value))}
                                helperText={translate(ResourcesIds.CHOOSE_CLINIC_PROMPT)}
                                margin="normal"
                                sx={clinicFieldStyles}
                            >
                                {tenants.map((tenant: TenantSummary) => (
                                    <MenuItem key={tenant.id} value={tenant.id}>{tenant.displayName}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            // Nothing selected yet means the second attempt would repeat the
                            // first and simply be asked the same question again.
                            disabled={!isValid || isSubmitting || isLoading || (!!tenants && selectedTenantId === '')}
                            startIcon={<LoginIcon />}
                            sx={signInButtonStyles}
                        >
                            {isSubmitting || isLoading ? translate(ResourcesIds.SIGNING_IN_TEXT) : translate(ResourcesIds.SIGN_IN_BUTTON)}
                        </Button>
                    </form>

                    <Box sx={linkBoxStyles}>
                        <Link component="button" variant="body2" onClick={() => navigate(forgotPasswordLink)} sx={getForgotPasswordLinkStyles(theme)}>
                            {translate(ResourcesIds.FORGOT_PASSWORD_TEXT)}
                        </Link>
                    </Box>

                    {/* Hidden, not gone -- see selfRegistrationEnabled. */}
                    {selfRegistrationEnabled && (
                        <Box sx={registerBoxStyles}>
                            <Typography variant="body2" color="text.secondary">
                                {translate(ResourcesIds.REGISTER_PROMPT)}{' '}
                                <Link component="button" variant="body2" onClick={() => navigate(registerLink)} sx={getRegisterLinkStyles(theme)}>
                                    {translate(ResourcesIds.REGISTER_LINK_TEXT)}
                                </Link>
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};